import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, usePrefersReducedMotion } from '@chakra-ui/react';
import type { LandingLayoutType } from 'ts/client/types';
import { LoadingIndicator } from 'ts/common/components';
import BaseLayout from 'ts/common/components/gallery/layouts/BaseLayout';
import { useClientGalleryServiceContext } from 'ts/common/contexts/ClientGalleryServiceContext';
import useCoverPhotoUrl from 'ts/common/hooks/useCoverPhotoUrl';
import type { ICoverPhoto } from 'ts/common/types';
import { noop } from 'ts/common/utils';
import CoverSlideshowGate from './CoverSlideshowGate';

export interface IIntroProps {
    landingLayoutType: LandingLayoutType;
    galleryTitle: string;
    brandName: string;
    eventTheme?: SpApi.Client.IEventTheme;
    coverPhoto: ICoverPhoto | false;
    isAdminRoute: boolean;
    isPreRegister: boolean;
    translateFilter: SpAngularJs.ITranslateFilter;
    onClick: () => void;
}

const Intro: FC<IIntroProps> = ({
    eventTheme,
    isPreRegister,
    isAdminRoute,
    translateFilter: t,
    ...layoutProps
}) => {
    const { coverPhoto } = layoutProps;
    const [isCoverPhotoLoaded, setIsCoverPhotoLoaded] = useState(false);

    const coverPhotoUrl = useCoverPhotoUrl(coverPhoto);

    // A visitor who prefers reduced motion never gets the player (see `CoverSlideshowBackground`),
    // so there is nothing to wait for — they go straight to the static cover photo.
    const prefersReducedMotion = usePrefersReducedMotion();
    // Live playback needs the client gallery's own services, which the Studio branding preview
    // doesn't have — it shows the still cover image instead.
    const clientGalleryServices = useClientGalleryServiceContext();

    const coverSlideshowToLoad =
        !prefersReducedMotion &&
        clientGalleryServices &&
        coverPhoto &&
        coverPhoto.mediaType === 'slideshow' &&
        coverPhoto.slideshowPhotoId
            ? coverPhoto
            : null;

    useEffect(() => {
        let imageLoader: HTMLImageElement | undefined;

        if (coverPhotoUrl) {
            imageLoader = new Image();

            imageLoader.src = coverPhotoUrl;

            imageLoader.onload = () => {
                setIsCoverPhotoLoaded(true);
            };
        } else {
            setIsCoverPhotoLoaded(true);
        }

        return () => {
            if (imageLoader) {
                imageLoader.onload = noop;
            }
        };
    }, [coverPhotoUrl]);

    const buttonTextLarge = useMemo(() => {
        if (isPreRegister && !isAdminRoute) {
            return t('register');
        }

        if (isPreRegister && isAdminRoute) {
            return t('enterAdminMode');
        }

        return t('viewGallery');
    }, [t, isAdminRoute, isPreRegister]);

    const buttonText = useMemo(() => {
        if (isPreRegister && !isAdminRoute) {
            return t('register');
        }

        if (isPreRegister && isAdminRoute) {
            return t('enter');
        }

        return t('viewGallery');
    }, [t, isAdminRoute, isPreRegister]);

    const loadingIndicator = (
        <Box position="fixed" transform="translate(-50%, -50%)" top="50vh" left="50vw" width="full">
            <LoadingIndicator squareClassName="theme-background-color-primary" />
        </Box>
    );

    if (!isCoverPhotoLoaded || !eventTheme) {
        return loadingIndicator;
    }

    const layout = (
        <BaseLayout
            {...layoutProps}
            buttonText={buttonText}
            buttonTextLarge={buttonTextLarge}
            colorScheme={eventTheme.colorScheme}
            coverPhotoUrl={coverPhotoUrl}
            eventBrandLogo={eventTheme.eventBrandLogo}
            logoAltText={eventTheme.logoAltText}
        />
    );

    if (!coverSlideshowToLoad) {
        return layout;
    }

    // Keeps the loading state up until the slideshow can start playing, so the intro opens on the
    // slideshow itself rather than showing its first-slide poster in the meantime.
    return (
        <CoverSlideshowGate coverPhoto={coverSlideshowToLoad} fallback={loadingIndicator}>
            {layout}
        </CoverSlideshowGate>
    );
};

export default Intro;
